// src/components/Dashboard/Patrolling/LivePatrolCard.js
import { Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle, MapPin, Zap } from 'lucide-react';
import React from 'react';
import { cn } from '../../../lib/utils';
import Card from '../../ui/card.js';

// Framer Motion variants
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const LivePatrolCard = ({ patrol, onClick, onShowOfficerInfo, isSelected, qrLookup = {} }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'in progress':
        return 'text-black';
      case 'not started':
        return 'text-black';
      case 'completed':
        return 'text-black';
      default:
        return 'text-black';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'in progress':
        return <Zap className="w-4 h-4 text-black" />;
      case 'not started':
        return <AlertTriangle className="w-4 h-4 text-black" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-black" />;
      default:
        return null;
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
    >
      <Card
        variant="light"
        className={cn(
          'relative p-4 flex flex-col justify-between transition-all duration-300 ease-in-out',
          isSelected ? 'border-2 border-white' : 'border border-black/20 hover:opacity-90',
          'w-full md:min-w-[340px] md:max-w-sm flex-shrink-0 cursor-pointer'
        )}
        onClick={() => onClick(patrol.id)}
      >
        <div className="flex justify-between items-center mb-2">
          <Typography variant="h6" className="text-black font-bold flex items-center gap-2">
            <MapPin className="w-5 h-5 text-black" />
            {patrol.patrol_name || `Patrol ${patrol.id.slice(-4)}`}
          </Typography>
          <div className={cn('text-sm font-semibold flex items-center gap-1', getStatusColor(patrol.status))}>
            {getStatusIcon(patrol.status)}
            {patrol.status.charAt(0).toUpperCase() + patrol.status.slice(1)}
          </div>
        </div>
        {patrol.siteName && patrol.siteName !== 'Unknown' && (
          <Typography variant="body1" className="text-black">
            <span className="font-medium text-black">Site:</span> {patrol.siteName}
          </Typography>
        )}
        {patrol.supervisorName && (
          <Typography variant="body2" className="text-gray-600 text-sm">
            <span className="font-medium">Supervisor:</span> {patrol.supervisorName}
          </Typography>
        )}
        {(patrol.patrol_start_timestamp || patrol.patrol_end_timestamp) && (
          <div className="mt-2 pt-2 border-t border-black/10">
            <Typography variant="body2" className="text-black text-sm">
              <span className="font-medium">Start:</span> {formatTimestamp(patrol.patrol_start_timestamp)}
            </Typography>
            <Typography variant="body2" className="text-black text-sm">
              <span className="font-medium">End:</span> {formatTimestamp(patrol.patrol_end_timestamp)}
            </Typography>
          </div>
        )}
        {/* QR Code Information */}
        {(patrol.start_qr_name || patrol.start_qr || patrol.end_qr_name || patrol.end_qr || (patrol.qr_sequence && patrol.qr_sequence.length > 0)) && (
          <div className="mt-2 pt-2 border-t border-black/10 flex flex-col gap-2">
            {(patrol.start_qr_name || patrol.start_qr) && patrol.start_qr !== 'N/A' && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-700">Start QR:</span>
                  <span className="text-xs text-gray-600 bg-green-100 px-2 py-1 rounded">
                    {patrol.start_qr_name || patrol.start_qr}
                  </span>
                </div>
                {patrol.start_qr_address && (
                  <div className="text-xs text-gray-500 ml-16">{patrol.start_qr_address}</div>
                )}
              </div>
            )}
            
            {/* Checkpoint QR Codes (intermediate QR codes between start and end) */}
            {patrol.qr_sequence && patrol.qr_sequence.length > 2 && (
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-gray-700">Checkpoints:</span>
                <div className="flex flex-wrap gap-1.5 ml-2">
                  {patrol.qr_sequence.slice(1, -1).map((qrId, index) => {
                    const qrInfo = qrLookup[qrId];
                    const qrName = qrInfo?.name || qrId;
                    const qrAddress = qrInfo?.address;
                    
                    return (
                      <div key={index} className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-medium text-blue-600">{index + 1}.</span>
                          <span className="text-xs text-gray-600 bg-blue-50 px-2 py-1 rounded border border-blue-200">
                            {qrName}
                          </span>
                        </div>
                        {qrAddress && (
                          <div className="text-xs text-gray-500 ml-4">{qrAddress}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {(patrol.end_qr_name || patrol.end_qr) && patrol.end_qr !== 'N/A' && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-700">End QR:</span>
                  <span className="text-xs text-gray-600 bg-red-100 px-2 py-1 rounded">
                    {patrol.end_qr_name || patrol.end_qr}
                  </span>
                </div>
                {patrol.end_qr_address && (
                  <div className="text-xs text-gray-500 ml-16">{patrol.end_qr_address}</div>
                )}
              </div>
            )}
          </div>
        )}
      </Card>
    </motion.div>
  );
};

export default LivePatrolCard;

